import React, { useState, useEffect } from 'react';
import defaultImage from '../assets/default2.png'; // Assuming you have a default image
import { getResearchPageSpec } from '../utils/professorResearchLink';
import { getCorpusMostRecent } from '../utils/parseProfessorCorpus';

const SearchCard = ({ professor, toggleSaved, isInitiallySaved }) => {
  const [imageSrc, setImageSrc] = useState(professor.photo || defaultImage);
  const [isSaved, setIsSaved] = useState(isInitiallySaved);

  useEffect(() => {
    // Ensure the initial state matches the parent's data
    setIsSaved(isInitiallySaved);
  }, [isInitiallySaved]);

  const handleToggleSave = async () => {
    const previous = isSaved;
    setIsSaved(!previous);
    try {
      await toggleSaved(professor);
    } catch (e) {
      setIsSaved(previous);
      console.error("Error toggling save:", e);
    }
  };

  const mostRecentPapers = getCorpusMostRecent(professor.corpus);

  const researchSpec = getResearchPageSpec(professor);

  return (
    <div className="search-card bg-white p-4 sm:p-6 nav:px-4 nav:py-5 md:px-5 lg:px-6 xl:p-8 rounded-lg shadow-md mb-4">
      <div className="flex flex-col w-full">
        <div className="flex flex-col nav:flex-row nav:items-start nav:gap-4 md:gap-4 w-full">
          {/* Small screens: photo + info stacked vertically */}
          <div className="nav:hidden w-full">
            <div className="image-container my-4 w-full">
              <img
                src={imageSrc}
                alt={professor.name}
                className="rounded-lg w-2/3 sm:w-1/2 h-auto object-cover mx-auto"
                onError={() => setImageSrc(defaultImage)}
              />
            </div>

            <div className="text-left px-2 sm:px-4">
              <p className="text-gray-700 text-base sm:text-lg mt-3">{professor.short_description}</p>
              <p className="text-gray-700 text-base sm:text-lg mt-3">{professor.additional_details}</p>

              <div className="mt-4">
                <h4 className="text-lg sm:text-xl text-gray-800 mt-3">Most Recent Papers:</h4>
                <ul className="list-disc pl-6 mt-2">
                  {mostRecentPapers.map((paper, index) => (
                    <li key={index} className="text-gray-700 text-base sm:text-lg">
                      {paper.title}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 pt-5 border-t border-gray-200">
                <div className="bg-gray-50 rounded-md p-4 text-gray-700 w-full min-w-0 text-base sm:text-lg">
                  <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 leading-snug">
                    {professor.name}
                  </h3>
                  <p className="mt-1 text-base sm:text-lg font-medium text-gray-800">
                    {professor.school}
                  </p>
                  <p className="text-base sm:text-lg text-gray-700">
                    {professor.department}
                  </p>
                {professor.email ? (
                  <p className="mt-2 text-sm sm:text-base break-all">
                    <a
                      href={`mailto:${professor.email}`}
                      className="text-gray-600 no-underline hover:underline"
                    >
                      {professor.email}
                    </a>
                  </p>
                ) : (
                  <p className="mt-2 text-gray-500">Email not available</p>
                )}
                {professor.website ? (
                  <p className="mt-2 text-base sm:text-lg">
                    <a
                      href={professor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Visit My Website
                    </a>
                  </p>
                ) : (
                  <p className="mt-2 text-base sm:text-lg">No Website Available</p>
                )}

                {researchSpec.type === 'none' ? (
                  <p className="mt-1 text-gray-500 text-base sm:text-lg">No research page available</p>
                ) : (
                  <p className="mt-1">
                    <a
                      href={researchSpec.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-base sm:text-lg"
                    >
                      {researchSpec.label}
                    </a>
                  </p>
                )}
              </div>
              </div>

              <div className="mt-5 w-full flex justify-center">
                <button
                  onClick={handleToggleSave}
                  className="bg-[#0196BF] text-white font-semibold text-base sm:text-lg px-6 py-2 rounded-full hover:bg-[#017BA3] transition z-20 w-3/4"
                >
                  {isSaved ? 'Unsave' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="hidden nav:block text-content flex-1 min-w-0 text-left px-2 sm:px-4 nav:px-0 nav:pr-0 mt-4 nav:mt-0">
            <h3 className="text-xl sm:text-2xl nav:text-3xl font-semibold text-gray-800">{professor.name}</h3>
            <p className="text-gray-700 text-base sm:text-lg mt-3 nav:mt-4">{professor.short_description}</p>
            <p className="text-gray-700 text-base sm:text-lg mt-3 nav:mt-4">{professor.additional_details}</p>

            <div className="mt-4 nav:mt-6">
              <h4 className="text-lg sm:text-xl text-gray-800 mt-3 nav:mt-4">Most Recent Papers:</h4>
              <ul className="list-disc pl-6 mt-2">
                {mostRecentPapers.map((paper, index) => (
                  <li key={index} className="text-gray-700 text-base sm:text-lg">
                    {paper.title}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="hidden nav:flex w-full shrink-0 nav:w-[35%] xl:max-w-[300px] 2xl:max-w-[320px] flex-col items-stretch text-left mt-2 nav:mt-0 min-w-0">
            <div className="image-container my-4 nav:my-4 w-full">
                <img
                  src={imageSrc}
                  alt={professor.name}
                  className="rounded-lg block w-full max-w-full h-auto"
                  onError={() => setImageSrc(defaultImage)} // Fallback to default image on error
                />
              </div>

            <div className="text-gray-700 w-full min-w-0 text-base sm:text-lg bg-gray-50 rounded-md p-4 mt-2 nav:mt-4">
              <p className="text-base sm:text-lg break-words">{professor.school}</p>
              <p className="text-base sm:text-lg break-words">{professor.department}</p>
              {professor.email ? (
                <p className="mt-2 text-sm sm:text-base break-all">
                  <a
                    href={`mailto:${professor.email}`}
                    className="text-gray-600 no-underline hover:underline"
                  >
                    {professor.email}
                  </a>
                </p>
              ) : (
                <p className="mt-2 text-gray-500 text-sm sm:text-base">Email not available</p>
              )}
              {professor.website ? (
                <p className="mt-2 text-base sm:text-lg break-words">
                  <a
                    href={professor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Visit My Website
                  </a>
                </p>
              ) : (
                <p className="mt-2 text-base sm:text-lg">No Website Available</p>
              )}

              {researchSpec.type === 'none' ? (
                <p className="mt-1 text-gray-500 text-base sm:text-lg break-words">No research page available</p>
              ) : (
                <p className="mt-1 break-words">
                  <a
                    href={researchSpec.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-base sm:text-lg"
                  >
                    {researchSpec.label}
                  </a>
                </p>
              )}
            </div>

            <div className="mt-4 w-full flex justify-center">
              <button
                onClick={handleToggleSave}
                className={`bg-[#0196BF] text-white font-semibold text-base sm:text-lg px-6 py-2 rounded-full hover:bg-[#017BA3] transition z-20 w-3/4 nav:w-full`}
              >
                {isSaved ? 'Unsave' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchCard;
